import React from 'react'
import GenericError from './GenericError/GenericError'
import Page from '@components/Page'
import Body from './Body/Body'
import Header from '@components/Header'
import { useTranslation } from 'react-i18next'
import Footer from '@components/Footer'
import { useIsPhoneOnly } from 'src/config/mediaQueries'
import { Button } from '@components/Button/Button'
import { TEST_IDS } from 'src/constants/testIds'
import { useConfig } from '@context/ConfigurationContext'
import useClinic from '@hooks/useClinic'

interface AppointmentConfirmationScreenProps {
  isValidating?: boolean
  error: string | undefined
  onBackClicked?: () => void
  onNextClicked: () => void
  isProcessing?: boolean
  showGenericError?: boolean
  canGoNext?: boolean
  bookButtonToken?: string
}

export const AppointmentConfirmationScreen = ({
  isValidating,
  error,
  onBackClicked,
  onNextClicked,
  isProcessing,
  children,
  showGenericError,
  canGoNext = true,
  bookButtonToken = 'book_appointment',
}: React.PropsWithChildren<AppointmentConfirmationScreenProps>) => {
  const { t } = useTranslation()
  const { clinicId } = useConfig()
  const { data: clinic, isLoading } = useClinic(clinicId)
  const isPhone = useIsPhoneOnly()
  const errorMessage = error && t(error, { defaultValue: 'api_errors.unexpected' })

  const ctaButton = (
    <Button
      text={t(bookButtonToken)}
      onClick={onNextClicked}
      data-test={TEST_IDS.buttonBook}
      disabled={isProcessing || !canGoNext}
      fullSize
    />
  )

  return (
    <Page
      header={
        <Header
          desktopHeadingText={t('booking_confirmation_page.title')}
          mobileHeadingText={t('booking_confirmation_page.title')}
          onBackClick={onBackClicked}
        />
      }
      body={
        showGenericError ? (
          <GenericError />
        ) : (
          <Body
            loading={isValidating || isLoading}
            clinic={clinic}
            ctaButton={!isPhone ? ctaButton : null}
            errorMessage={errorMessage}
          >
            {children}
          </Body>
        )
      }
      footer={isPhone ? <Footer button={ctaButton} /> : undefined}
    />
  )
}
